import React, {useCallback, useContext, useEffect, useState} from 'react';
import {Col, Container, Form, Row} from "react-bootstrap";
import {useHistory, useLocation} from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import PageHeader from "components/PageHeader";
import {VALIDATION_IN_JS} from "utils/Constants";
import {CLEARING_SYSTEM_CREATE_URL, CLEARING_SYSTEM_SEARCH_URL, COUNTRY_LIST_COUNTRY_NAME} from "service/UrlConstant";
import {SecurityContext} from "context/security";
import {ToastContext} from "context/Toast";
import {ClearingSystemContext, CLEARING_SYSTEM_HOME_PAGE} from "./context";
import {retrieveErrorMessage} from "../../../utils/ErrorUtil";
import {USABA_CLEARING_SYSTEM_CODE, USABA_CLEARING_SYSTEM_ID_PREFIX} from "pages/admin/wiretemplate/AddNewWireTemplateStep1";
import useClearingSystemCodes from "./hooks/useClearingSystemCodes";

const initCreateState = {
    clearingSystemCode: USABA_CLEARING_SYSTEM_CODE,
    clearingSystemId: USABA_CLEARING_SYSTEM_ID_PREFIX,
    agentName: '',
    agentCity: '',
    agentProvince: '',
    agentCountry: 'US'
}

// USABA clearing system id rules (mirrors AddNewWireTemplateStep2.js):
// the id must start with the FW prefix followed by digits only.
const USABA_CLEARING_SYSTEM_ID_FORMAT_ERROR =
    `Clearing System ID must start with ${USABA_CLEARING_SYSTEM_ID_PREFIX} followed by digits`;

const isUsabaCode = (code) =>
    (code || '').toString().trim().toUpperCase() === USABA_CLEARING_SYSTEM_CODE;

const isValidUsabaClearingSystemId = (id) =>
    new RegExp(`^${USABA_CLEARING_SYSTEM_ID_PREFIX}\\d+$`).test((id || '').toString().trim());

// Force the FW prefix and keep only the digits the user types after it.
const formatUsabaClearingSystemId = (value) => {
    let raw = (value || '').toString();
    if (raw.length < USABA_CLEARING_SYSTEM_ID_PREFIX.length) {
        raw = USABA_CLEARING_SYSTEM_ID_PREFIX
    }
    const afterPrefix = raw.toUpperCase().startsWith(USABA_CLEARING_SYSTEM_ID_PREFIX)
                                   ? raw.slice(USABA_CLEARING_SYSTEM_ID_PREFIX.length)
                                   : raw

    return `${USABA_CLEARING_SYSTEM_ID_PREFIX}${afterPrefix}`;
}

export default function ClearingSystemCreatePanel() {

    const {
        displayClearingSystemPanelPage,
        clearingSystemFilterState,
        clearingSystemResultsDisplayState,
        setClearingSystemResultsDisplayState,
        setShowGridToolbar
    } = useContext(ClearingSystemContext);
    const {makePostRequest, makeGetRequestNoParams} = useContext(SecurityContext);
    const {error, success} = useContext(ToastContext);

    const history = useHistory();
    const location = useLocation();
    // When launched from another screen (e.g. the wire template wizard step 2), a
    // returnTo path is passed so we can navigate the user back after create/cancel.
    const returnTo = location.state && location.state.returnTo;

    const [createRecord, setCreateRecord] = useState({...initCreateState});
    const [formErrors, setFormErrors] = useState({});
    const [countries, setCountries] = useState([]);
    const {getClearingSystemCodeList, clearingSystemCodeOptions} = useClearingSystemCodes(createRecord.clearingSystemCode);

    const getCountries = useCallback(() => {
        const onSuccessResponse = (res) => {
            if (res.data) {
                setCountries(res.data);
            }
        }

        const onErrorResponse = (err) => {
            console.log("Error on get unique iso country code country name: ", err);
        }

        makeGetRequestNoParams(COUNTRY_LIST_COUNTRY_NAME, onSuccessResponse, onErrorResponse)
    }, [makeGetRequestNoParams])

    useEffect(() => {
        getCountries()
        getClearingSystemCodeList()
    }, [getCountries, getClearingSystemCodeList])


    const handleOnChange = (e) => {
        const {name, value} = e.target;
        setCreateRecord(prevState => {
            const nextState = {...prevState, [name]: value};

            if (name === 'clearingSystemCode') {
                // When the code is USABA, the Clearing System ID is prefixed with FW
                // and only digits can follow. Adjust the ID whenever the code changes.
                if (isUsabaCode(value)) {
                    nextState.clearingSystemId = formatUsabaClearingSystemId(prevState.clearingSystemId);
                } else if (isUsabaCode(prevState.clearingSystemCode)) {
                    // Leaving USABA: drop the forced FW prefix
                    nextState.clearingSystemId = '';
                }
            } else if (name === 'clearingSystemId' && isUsabaCode(nextState.clearingSystemCode)) {
                nextState.clearingSystemId = formatUsabaClearingSystemId(value);
            }

            return nextState;
        });
        setFormErrors(prevFormErrors => {
            const {[name]: _, ...otherFormErrors} = prevFormErrors;
            return otherFormErrors;
        })
    }

    const validateSubmission = (record) => {
        let errors = {};
        if (!record.clearingSystemCode || !record.clearingSystemCode.trim())
            errors['clearingSystemCode'] = 'Please input Clearing System Code';
        else if (record.clearingSystemCode.trim().length > 50)
            errors['clearingSystemCode'] = 'Please limit your input to 50 characters or fewer';

        if (!record.clearingSystemId || !record.clearingSystemId.trim())
            errors['clearingSystemId'] = 'Please input Clearing System ID';
        else if (record.clearingSystemId.trim().length > 50)
            errors['clearingSystemId'] = 'Please limit your input to 50 characters or fewer';
        else if (isUsabaCode(record.clearingSystemCode) && !isValidUsabaClearingSystemId(record.clearingSystemId))
            errors['clearingSystemId'] = USABA_CLEARING_SYSTEM_ID_FORMAT_ERROR;

        if (!record.agentName || !record.agentName.trim())
            errors['agentName'] = 'Please input Agent Name';
        else if (record.agentName.trim().length > 255)
            errors['agentName'] = 'Please limit your input to 255 characters or fewer';

        if (!record.agentCity || !record.agentCity.trim())
            errors['agentCity'] = 'Please input Agent City';
        else if (record.agentCity.trim().length > 35)
            errors['agentCity'] = 'Please limit your input to 35 characters or fewer';

        if (!record.agentProvince || !record.agentProvince.trim())
            errors['agentProvince'] = 'Please input Agent Province';
        else if (record.agentProvince.trim().length > 35)
            errors['agentProvince'] = 'Please limit your input to 35 characters or fewer';

        if (!record.agentCountry || !record.agentCountry.trim())
            errors['agentCountry'] = 'Please select Agent Country';
        else if (record.agentCountry.trim().length > 2)
            errors['agentCountry'] = 'Please use the 2 character country code';

        return errors;
    }

    const handleCancel = () => {
        if (returnTo) {
            history.push(returnTo);
            return;
        }
        displayClearingSystemPanelPage(CLEARING_SYSTEM_HOME_PAGE);
    }

    const handleSearch = () => {
        const params = {
            clearingSystemCode: clearingSystemFilterState.clearingSystemCode || null,
            clearingSystemId: clearingSystemFilterState.clearingSystemId || null,
            agentName: clearingSystemFilterState.agentName || null
        }

        const onSuccessResponse = (response) => {
            setShowGridToolbar(response.data.length >= 0);
            const displayState = JSON.parse(JSON.stringify(clearingSystemResultsDisplayState));
            displayState.resData = ([...response.data]);
            displayState.search = params;
            setClearingSystemResultsDisplayState(displayState);
        }
        const onErrorResponse = (err) => {
            console.error(err);
            if (error) error("Error refreshing clearing system data");
        }

        makePostRequest(params, CLEARING_SYSTEM_SEARCH_URL, onSuccessResponse, onErrorResponse);
    }

    const createHandler = (e) => {
        e.preventDefault();
        let errors = {};
        if (VALIDATION_IN_JS) {
            errors = validateSubmission(createRecord);
        }
        setFormErrors(errors);
        if (Object.keys(errors).length !== 0)
            return;

        const onSuccess = () => {
            if (success) success("Clearing system created successfully");
            // Capture the values that were just created before resetting the form so
            // they can be handed back to the screen that launched this create flow.
            const createdClearingSystem = {
                clearingSystemCode: createRecord.clearingSystemCode,
                clearingSystemId: createRecord.clearingSystemId,
                agentName: createRecord.agentName,
                agentCity: createRecord.agentCity,
                agentProvince: createRecord.agentProvince,
                agentCountry: createRecord.agentCountry
            };
            setCreateRecord({...initCreateState});
            if (returnTo) {
                // Return to the screen that launched this create flow (e.g. wire
                // template step 2/5) and pass the newly created code/id so that screen
                // can pre-populate its clearing system fields.
                history.push(returnTo, {createdClearingSystem});
                return;
            }
            handleSearch();
            displayClearingSystemPanelPage(CLEARING_SYSTEM_HOME_PAGE);
        }
        const onError = (err) => {
            console.error(err);
            let errMsg = "Error creating clearing system"
            errMsg = retrieveErrorMessage(err, errMsg)
            if (error) error(errMsg);
        }

        const createParams = {
            clearingSystemCode: createRecord.clearingSystemCode,
            clearingSystemId: createRecord.clearingSystemId,
            agentName: createRecord.agentName || null,
            agentCity: createRecord.agentCity || null,
            agentProvince: createRecord.agentProvince || null,
            agentCountry: createRecord.agentCountry || null
        }

        makePostRequest(createParams, CLEARING_SYSTEM_CREATE_URL, onSuccess, onError);
    }

    const renderField = (label, name, required, maxLength) => (
        <Col xs={12} sm={12} md={6} className={'py-0'}>
            <Form.Group controlId={`formClearingSystemCreate-${name}`}>
                <label className='my-0'>{label}{required && <span className='red-cell'> *</span>}</label>
                <Form.Control
                    type="text"
                    maxLength={maxLength}
                    value={createRecord[name]}
                    name={name}
                    onChange={handleOnChange}
                    isInvalid={!!formErrors[name]}
                />
                <Form.Control.Feedback type={"invalid"}>
                    <b><i className="bi bi-exclamation-circle"></i>{formErrors[name]}</b>
                </Form.Control.Feedback>
            </Form.Group>
        </Col>
    );

    // Clearing System Code is an editable combobox: it defaults to USABA and lets the
    // user either pick an existing code from the dropdown or type a brand-new code.
    const renderClearingSystemCodeField = (label, name, required) => {
        const selectedValue = createRecord[name]
            ? {label: createRecord[name], value: createRecord[name]}
            : null;
        return (
            <Col xs={12} sm={12} md={6} className={'py-0'}>
                <Form.Group controlId={`formClearingSystemCreate-${name}`}>
                    <label className='my-0'>{label}{required && <span className='red-cell'> *</span>}</label>
                    <CreatableSelect
                        isClearable
                        name={name}
                        inputId={`formClearingSystemCreate-${name}`}
                        options={clearingSystemCodeOptions}
                        value={selectedValue}
                        onChange={(option) =>
                            handleOnChange({target: {name, value: option ? option.value : ''}})}
                        placeholder="Select or enter code"
                        formatCreateLabel={(input) => `Use "${input}"`}
                        maxMenuHeight={200}
                    />
                    {formErrors[name] &&
                    <div className="invalid-feedback d-block">
                        <b><i className="bi bi-exclamation-circle"></i>{formErrors[name]}</b>
                    </div>}
                </Form.Group>
            </Col>
        );
    };

    const renderCountryField = (label, name, required) => (
        <Col xs={12} sm={12} md={6} className={'py-0'}>
            <Form.Group controlId={`formClearingSystemCreate-${name}`} className="td-select">
                <label className='my-0'>{label}{required && <span className='red-cell'> *</span>}</label>
                <Form.Control
                    as="select"
                    value={createRecord[name]}
                    name={name}
                    onChange={handleOnChange}
                    isInvalid={!!formErrors[name]}
                >
                    <option value={''}>Select</option>
                    {countries.map(country => (
                        <option key={country.countryCode} value={country.countryCode}>{country.country}</option>
                    ))}
                </Form.Control>
                <Form.Control.Feedback type={"invalid"}>
                    <b><i className="bi bi-exclamation-circle"></i>{formErrors[name]}</b>
                </Form.Control.Feedback>
            </Form.Group>
        </Col>
    );

    return (<>
        <Container fluid>
            <PageHeader text={"Create Clearing System"}/>
            <Form onSubmit={createHandler}>
                <Row>
                    {renderClearingSystemCodeField('Clearing System Code', 'clearingSystemCode', true)}
                    {renderField('Clearing System ID', 'clearingSystemId', true, 50)}
                    {renderField('Agent Name', 'agentName', true, 255)}
                    {renderField('Agent City', 'agentCity', true, 35)}
                    {renderField('Agent Province', 'agentProvince', true, 35)}
                    {renderCountryField('Agent Country', 'agentCountry', true)}
                </Row>
                <Row className="mt-3">
                    <Col xs={6} sm={6} md={3}>
                        <button type="button" className="btn btn-block td-btn-secondary-clear my-3"
                                onClick={handleCancel}>Cancel
                        </button>
                    </Col>
                    <Col xs={6} sm={6} md={3}>
                        <button type="submit" className="btn btn-block td-btn-primary-light my-3">Create</button>
                    </Col>
                </Row>
            </Form>
        </Container>
    </>);
}

